import { Box, Text } from '@factorialco/deprecated-design-system'
import packageIconUrl from 'assets/images/package.svg'
import type { Currency } from 'dinero.js'
import moment from 'moment'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { formatTime } from 'modules/core/lib/formatTime'
import i18n from 'modules/core/lib/i18n'

import BlankSlate from 'modules/payroll_bundle/components/BlankSlate'
import { usePayrollResults } from 'modules/payroll_bundle/lib/PayrollResultContext'
import { UPDATE_DATA } from 'modules/payroll_bundle/lib/constants'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import { documentAuthor } from 'modules/payroll_bundle/lib/utils'
import EmptyState from 'modules/payroll_bundle/pages/ResultsAndPaymentsPage/ResultsPage/Empty'
import resultsCollection from 'modules/payroll_bundle/store/collections/results'

import ResultContents from './ResultContents'

interface Props {
  currency: Currency
}

const Results = ({ currency }: Props) => {
  const { resultDispatch, uploadedPayrollResults } = usePayrollResults()
  const { currentPeriod } = usePolicyPeriods()
  const hasResults = resultsCollection.filterByPolicyPeriod(currentPeriod?.id).length > 0
  const hasUploadedPayrollResults = uploadedPayrollResults.length > 0

  const { access } = useCurrentSession()

  const openResultFilesModal = () => {
    resultDispatch({
      type: UPDATE_DATA,
      payload: {
        showResultsFilesModal: true,
      },
    })
  }

  if (hasUploadedPayrollResults && !hasResults) {
    const lastUploadedPayrollResult = uploadedPayrollResults[uploadedPayrollResults.length - 1]
    const lastUploadedPayrollResultBookkeeperName = lastUploadedPayrollResult
      ? documentAuthor(lastUploadedPayrollResult)?.fullName()
      : ''
    const lastUploadedPayrollResultTime = lastUploadedPayrollResult?.attributes?.created_at
    const lastUploadedPayrollResultDate = moment(lastUploadedPayrollResultTime)

    const uploadedResultsBookkeepers = uploadedPayrollResults.map((r) => {
      return documentAuthor(r)?.fullName()
    })
    const differentUploadedResultsBookkeepers = new Set(uploadedResultsBookkeepers).size

    const messageTranslation = () => {
      return i18n.tView(
        'payroll_bundle.payroll_results.results_upload.available_results_description',
        {
          bookkeeperName: (
            <Text.Normal>
              {differentUploadedResultsBookkeepers > 1
                ? i18n.t(
                    'payroll_bundle.payroll_results.result_files.pending_result_files_multiple_bookeepers',
                    {
                      bookkeeperName: lastUploadedPayrollResultBookkeeperName,
                      extraBookeeperNumber: differentUploadedResultsBookkeepers - 1,
                    }
                  )
                : lastUploadedPayrollResultBookkeeperName}
            </Text.Normal>
          ),
          uploadDate: <Text.Normal>{lastUploadedPayrollResultDate.format('ll')}</Text.Normal>,
          uploadTime: (
            <Text.Normal>
              {formatTime(access, lastUploadedPayrollResultDate.format('HH:mm'))}
            </Text.Normal>
          ),
          bold_text: (
            <strong>
              {uploadedPayrollResults.length > 1
                ? i18n.t(
                    'payroll_bundle.payroll_results.results_upload.multiple_available_results_description_emphasis',
                    {
                      fileAmount: uploadedPayrollResults.length,
                    }
                  )
                : lastUploadedPayrollResult?.filename}
            </strong>
          ),
        }
      )
    }

    return (
      <BlankSlate
        label={i18n.t('payroll_bundle.payroll_results.results_upload.available_results_title')}
        iconUrl={packageIconUrl}
        withIcon
        message={messageTranslation()}
        action={openResultFilesModal}
        actionText={i18n.t('payroll_bundle.payroll_results.results_upload.manage_result')}
        uploaded
      />
    )
  }

  if (hasResults) {
    return (
      <Box gap="s40">
        <ResultContents currency={currency} />
      </Box>
    )
  }

  return <EmptyState />
}

export default observer(Results)
